import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import { DeviceProfileService } from '../../../../../../../services/device-profile.service';
import { DeviceService } from '../../../../../../../services/device.service';
import { InputSelectComponent } from '../../../../../../../shared/components/input-select/input-select';
import { InputTextComponent } from '../../../../../../../shared/components/input-text/input-text.component';
import { TableComponent } from '../../../../../../../shared/components/table/table';
import { formatTimeAgo } from '../../../../../../../shared/utils/date.util';
import { DeviceDto, DeviceRow } from '../../../../../../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../../../../../../types/dtos/deviceProfile.dto';
import { DeviceStatus } from '../../../../../../../types/enums/device.enum';
import { InputSelectInterface, InputTextInterface } from '../../../../../../../types/interfaces/input.interface';
import { TableConfig, TableRowDto, TableTagSeverity } from '../../../../../../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../../../../../../dashboard/dashboard-box/dashboard-box.component';

@Component({
  selector: 'app-report-filter-device',
  imports: [
    TableComponent,
    DashboardBoxComponent,
    InputSelectComponent,
    InputTextComponent,
    TranslatePipe,
  ],
  templateUrl: './filter-device.html',
  styleUrl: './filter-device.css',
})
export class ReportFilterDevice {
  @Input({required: true}) formGroup!: FormGroup;
  public loading: boolean = true;
  public allDevices: DeviceDto[] = [];
  public deviceList: DeviceDto[] = [];
  public tableRows: TableRowDto<DeviceRow>[] = [];
  public tableConfig: TableConfig<DeviceRow> = {
    columns: [
      {
        key: 'status',
        isTranslated: true,
        title: 'common.table.status.label',
        isTag: true,
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideValue: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
        sticky: 'left',
      },
      {
        key: 'serial_number',
        isTranslated: true,
        title: 'common.table.serielNumber.label',
      },
      {
        key: 'device_eui',
        isTranslated: true,
        title: 'common.table.deviceEui.label',
        visible: false,
      },
      {
        key: 'model',
        isTranslated: true,
        title: 'common.table.model.label',
      },
      {
        key: 'address',
        isTranslated: true,
        title: 'common.table.address.label',
      },
      {
        key: 'last_measurement',
        isTranslated: true,
        title: 'common.table.lastMeasurement.label',
        overrideValue: (row: DeviceRow) => {
          if (!row.last_measurement) {
            return '-';
          }

          let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

          if (!lastMeasurementTime || lastMeasurementTime === '-') {
            return `${ (row.last_measurement as number) / 1000 } m³`;
          }

          lastMeasurementTime = lastMeasurementTime.charAt(0)
            .toLowerCase() + lastMeasurementTime.slice(1);

          return `${ (row.last_measurement as number) / 1000 } m³ ${ lastMeasurementTime }`;
        },
      },
    ],
    mobile: {
      title: {
        key: 'serial_number',
        tag: 'Status',
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideTag: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
      },
      description: {
        key: 'model',
      },
      sections: [
        {
          key: 'address',
          isTranslated: true,
          title: 'common.table.address.label',
        },
        {
          key: 'last_measurement',
          isTranslated: true,
          title: 'common.table.lastMeasurement.label',
          overrideValue: (row: DeviceRow) => {
            if (!row.last_measurement) {
              return '-';
            }

            let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

            if (!lastMeasurementTime || lastMeasurementTime === '-') {
              return `${ (row.last_measurement as number) / 1000 } m³`;
            }

            lastMeasurementTime = lastMeasurementTime.charAt(0)
              .toLowerCase() + lastMeasurementTime.slice(1);

            return `${ (row.last_measurement as number) / 1000 } m³ ${ lastMeasurementTime }`;
          },
        },
      ],
    },
    pagination: true,
    showRows: 25,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    scrollHeight: '55vh',
    emptyMessage: 'common.table.empty-message.device',
    lazy: false,
    sortableColumns: true,
  };
  private modelOptions: InputSelectInterface['options'] = [];
  private _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private _deviceService: DeviceService,
    private _deviceProfileService: DeviceProfileService,
  ) {

  }

  public get statusSelect(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      onInput: () => this._filterData(),
      isTranslated: true,
      label: 'common.input.status-select.label',
      id: 'statusSelect',
      iconLeft: 'account_circle',
      showClear: true,
      options: Object.values(DeviceStatus)
        .sort()
        .map((action) => ({
          label: action.slice(0, 1)
            .toUpperCase() + action.slice(1),
          value: action.toLowerCase(),
        })),
      required: false,
    };
  };

  public get modelSelect(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      onInput: () => this._filterData(),
      isTranslated: true,
      label: 'common.input.model.label',
      id: 'modelSelect',
      iconLeft: 'account_circle',
      showClear: true,
      options: this.modelOptions,
      required: false,
    };
  };

  public get searchConfig(): InputTextInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.search-device.label',
      id: 'search',
      iconLeft: 'search',
      showClear: true,
      type: 'text',
      onInput: () => this._filterData(),
    };
  };

  public get deviceCountLabel(): string {
    return `${ this.deviceList.length } von ${ this.allDevices.length } Geräte`;
  }

  public resetFilter(): void {
    this.formGroup.reset();
    this._filterData();
  }

  public ngAfterViewInit(): void {
    this._deviceService.devices$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (data: DeviceDto[]) => {
          this.allDevices = data;
          this._filterData();
          this.loading = false;
        },
        error: (error: unknown) => {
          console.error('Error fetching devices:', error);
        },
      });

    this._deviceProfileService.devicesProfile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((devicesProfile: DeviceProfileDto[]) => {
        this.modelOptions = [
          {
            isTranslated: true,
            label: 'common.label.no-models',
            value: '-1',
          },
          ...devicesProfile
            .filter((profile: DeviceProfileDto) => profile.model)
            .map((profile: DeviceProfileDto) => ({
              label: profile.model ?? '',
              value: profile.id.toString(),
            })),
        ];
      });
  }

  public getDeviceModelName(deviceProfileId: number): string {
    const deviceProfile: DeviceProfileDto | null =
      this._deviceProfileService.getDeviceProfileById(deviceProfileId);

    return deviceProfile?.model ?? '-';
  }

  private _setTableRows(): void {
    this.tableRows = this.deviceList.map((device: DeviceDto) => {
      return {
        id: device.id,
        status: device.status,
        device_eui: device.device_eui,
        serial_number: device.serial_number ?? '-',
        model: device.device_profile_id ? this.getDeviceModelName(device.device_profile_id) : '-',
        address: device.position?.address ?? '-',
        alarms: 0,
        last_measurement: device.last_measurement?.['METER_VALUE_1']
          ? Number(device.last_measurement['METER_VALUE_1'])
          : null,
        last_measurement_received_time: device.last_measurement_received_time ?? undefined,
        last_sigi_send_time: device.last_sigi_send_time ?? undefined,
      };
    });
  }

  private _filterData(): void {
    let {
      statusSelect,
      modelSelect,
      search,
    } = this.formGroup.value;

    modelSelect = Number(modelSelect);

    const normalizedSearch: string = String(search ?? '')
      .trim()
      .toLowerCase();

    this.deviceList = this.allDevices.filter((device: DeviceDto) => {
      const matchesStatus: boolean = !statusSelect || device.status === statusSelect;
      const matchesModel: boolean = !modelSelect || (device.device_profile_id ?? -1) === modelSelect;

      const deviceEui: string = device.device_eui ?? '';
      const serialNumber: string = device.serial_number ?? '';
      const model: string = device.device_profile_id
        ? this.getDeviceModelName(device.device_profile_id)
        : '-';
      const address: string = device.position?.address ?? '-';
      const status: string = device.status ?? '';

      const matchesSearch: boolean = !normalizedSearch || [
        deviceEui,
        serialNumber,
        model,
        address,
        status,
      ].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(normalizedSearch),
      );

      return matchesStatus && matchesModel && matchesSearch;
    });

    this._setTableRows();
  }
}
